const SPLASH_ID = 'app-splash';
const DONE_CLASS = 'app-splash--done';
// Keep in sync with the #app-splash transition duration in src/index.html.
const FADE_MS = 300;
// Keep in sync with the .app-splash-content animation delay in src/index.html.
const SHOW_DELAY_MS = 300;
// Anti-flicker: once the content has become visible, keep it up at least this
// long so a fast boot never blinks the splash for a few frames.
const MIN_VISIBLE_MS = 500;

/** How long a boot may run with no completed navigation before the watchdog
 *  declares it dead. Generous: covers slow networks and the OIDC redirect
 *  round-trip, so it only ever fires on a genuinely wedged boot (e.g. /me and
 *  its JWT fallback both failing). */
const WATCHDOG_MS = 30_000;

/**
 * Boot watchdog: runs `onDeadEnd` after WATCHDOG_MS unless cancelled first.
 * The splash is dismissed by the first completed navigation — if none ever
 * completes, nothing would take it down; the watchdog is that backstop
 * (AppComponent points it at the load-failed error page, whose navigation
 * then dismisses the splash through the normal path). Returns the cancel
 * function; call it on the first NavigationEnd and on destroy.
 */
export function startSplashWatchdog(onDeadEnd: () => void, delayMs = WATCHDOG_MS): () => void {
  const id = setTimeout(onDeadEnd, delayMs);
  return () => clearTimeout(id);
}

/**
 * Fades out and removes the boot splash from src/index.html. The element lives
 * outside <app-root>, so Angular never renders or destroys it; this is the one
 * place that touches it. Idempotent and a no-op when the splash is absent
 * (unit tests, already dismissed).
 *
 * Flicker control (delay + minimum display time): the splash content only
 * fades in after SHOW_DELAY_MS (CSS animation delay). If the app is ready
 * before that, nothing was ever visible and the overlay is removed instantly;
 * its background matches the page background, so the removal is seamless.
 * If the content did appear, it stays up until it has been visible for
 * MIN_VISIBLE_MS before fading, so it reads as deliberate rather than a blink.
 */
export function dismissSplash(doc: Document = document): void {
  const el = doc.getElementById(SPLASH_ID);
  if (!el || el.dataset['dismissing']) return;
  el.dataset['dismissing'] = 'true';

  // performance.now() is ~time since navigation start, which is when the
  // content's CSS fade-in delay started counting too.
  const elapsed = performance.now();
  if (elapsed < SHOW_DELAY_MS) {
    el.remove();
    return;
  }

  const fadeOut = () => {
    el.classList.add(DONE_CLASS);
    const remove = () => el.remove();
    // transitionend covers the normal path; the timeout is the safety net when
    // the transition never fires (prefers-reduced-motion zeroes durations,
    // hidden tabs throttle rendering). Removing twice is harmless.
    el.addEventListener('transitionend', remove, { once: true });
    setTimeout(remove, FADE_MS + 150);
  };

  const visibleFor = elapsed - SHOW_DELAY_MS;
  const holdFor = MIN_VISIBLE_MS - visibleFor;
  if (holdFor > 0) {
    setTimeout(fadeOut, holdFor);
  } else {
    fadeOut();
  }
}
